/**
 * Collection Navigation JavaScript
 * Handles auto-expand functionality and mobile collapsible menu
 */

class CollectionNavigation {
  constructor(navigation = null) {
  this.navigation =
    navigation || document.querySelector('.collection-navigation');
    this.details = document.querySelectorAll('.collection-nav-details');
    this.mobileBreakpoint = 990;

    // Record the current responsive state.
    // This prevents mobile browser toolbar changes from closing the menu.
    this.previousMobileState = null;

    if (!this.navigation) return;

    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    this.setupExpandableCollections();
    this.setupAccordion();
    this.setupAutoExpand();
    this.setupMobileCollapsible();

    // Initialise responsive state once.
    this.previousMobileState = this.isMobile();

    window.addEventListener('resize', this.handleResize);
  }

  setupExpandableCollections() {
    // No click handlers - only auto-expand when on collection page
  }

  setupAutoExpand() {
    const currentUrl = window.location.pathname;
    const activeLink = document.querySelector('.collection-nav-link.active');

    if (!activeLink) {
      return;
    }

    const currentHandle = this.getCurrentCollectionHandle(
      currentUrl,
      activeLink
    );

    if (!currentHandle) {
      return;
    }

    this.autoExpandParentOf(currentHandle);
    this.autoExpandCollection(currentHandle);
  }

  getCurrentCollectionHandle(url, activeLink) {
    if (activeLink && activeLink.dataset.collectionHandle) {
      return activeLink.dataset.collectionHandle;
    }

    const urlMatch = url.match(/\/collections\/([^/?]+)/);

    if (urlMatch) {
      try {
        return decodeURIComponent(urlMatch[1]);
      } catch (error) {
        return urlMatch[1];
      }
    }

    if (activeLink && activeLink.href) {
      const hrefMatch = activeLink.href.match(/\/collections\/([^/?]+)/);

      if (hrefMatch) {
        try {
          return decodeURIComponent(hrefMatch[1]);
        } catch (error) {
          return hrefMatch[1];
        }
      }
    }

    return null;
  }

  autoExpandParentOf(childHandle) {
    const currentLink = document.querySelector(
      `[data-collection-handle="${childHandle}"]`
    );

    if (!currentLink) return;

    const parentChain = this.buildParentChain(childHandle);

    parentChain.forEach((parentHandle) => {
      this.expandCollection(parentHandle);
    });
  }

  buildParentChain(collectionHandle) {
    const parentChain = [];
    const visitedHandles = new Set();
    let currentHandle = collectionHandle;

    while (currentHandle) {
      if (visitedHandles.has(currentHandle)) {
        break;
      }

      visitedHandles.add(currentHandle);

      const currentLink = document.querySelector(
        `[data-collection-handle="${currentHandle}"]`
      );

      if (!currentLink) break;

      const parentHandle = currentLink.dataset.parentCollection;

      if (parentHandle) {
        parentChain.unshift(parentHandle);
        currentHandle = parentHandle;
      } else {
        break;
      }
    }

    return parentChain;
  }

  expandCollection(collectionHandle) {
    const subcategoriesContainer = document.querySelector(
      `[data-collection-id="${collectionHandle}"]`
    );

    if (subcategoriesContainer) {
      subcategoriesContainer.style.display = 'block';

      const collectionLink = document.querySelector(
        `[data-collection-handle="${collectionHandle}"]`
      );

      if (collectionLink) {
        collectionLink.classList.add('expanded');
      }
    }
  }

  autoExpandCollection(currentHandle) {
    this.expandCollection(currentHandle);
  }

  setupAccordion() {
    this.details.forEach((detail) => {
      if (detail.dataset.accordionInitialized === 'true') {
        return;
      }

      const summary = detail.querySelector('.collection-nav-summary');

      if (summary) {
        summary.addEventListener('click', (event) => {
          const isChevron = event.target.closest('svg');

          if (isChevron) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleAccordion(detail);
          }
        });

        detail.dataset.accordionInitialized = 'true';
      }
    });

    const level3Details = document.querySelectorAll(
      '.collection-nav-level3'
    );

    level3Details.forEach((detail) => {
      if (detail.dataset.accordionInitialized === 'true') {
        return;
      }

      const summary = detail.querySelector(
        '.collection-nav-summary-level3'
      );

      if (summary) {
        summary.addEventListener('click', (event) => {
          const isChevron = event.target.closest('svg');

          if (isChevron) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleAccordion(detail);
          }
        });

        detail.dataset.accordionInitialized = 'true';
      }
    });
  }

  toggleAccordion(detail) {
    const isOpen = detail.hasAttribute('open');

    if (isOpen) {
      detail.removeAttribute('open');
    } else {
      detail.setAttribute('open', '');
    }

    const subcategories = detail.querySelector(
      '.collection-nav-subcategories'
    );

    if (!subcategories) return;

    if (isOpen) {
      subcategories.style.maxHeight = `${subcategories.scrollHeight}px`;

      window.setTimeout(() => {
        subcategories.style.maxHeight = '0';
      }, 10);
    } else {
      subcategories.style.maxHeight = `${subcategories.scrollHeight}px`;

      window.setTimeout(() => {
        subcategories.style.maxHeight = 'none';
      }, 300);
    }
  }

setupMobileCollapsible() {
  const header = this.navigation.querySelector('.collection-nav-header');
  const content = this.navigation.querySelector('.collection-nav-content');

  if (!header || !content) return;

  if (header.dataset.mobileToggleInitialized === 'true') {
    return;
  }

  let startX = 0;
  let startY = 0;
  let hasMoved = false;
  let touchingContent = false;

  this.navigation.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];

      if (!touch) return;

      startX = touch.clientX;
      startY = touch.clientY;
      hasMoved = false;

      touchingContent = Boolean(
        event.target.closest('.collection-nav-content')
      );
    },
    { passive: true }
  );

  this.navigation.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches[0];

      if (!touch) return;

      const distanceX = Math.abs(touch.clientX - startX);
      const distanceY = Math.abs(touch.clientY - startY);

      if (distanceX > 5 || distanceY > 5) {
        hasMoved = true;
      }
    },
    { passive: true }
  );

  header.addEventListener('click', (event) => {
    /*
     * 若 collection-nav-content 因 HTML 結構錯誤被包在 header 裡，
     * 點擊或滑動列表時 click 會冒泡到 header。
     * 這裡直接排除列表內容。
     */
    if (event.target.closest('.collection-nav-content')) {
      return;
    }

    /*
     * 手指是在列表內容內開始操作，不允許收合。
     */
    if (touchingContent) {
      touchingContent = false;
      hasMoved = false;
      return;
    }

    /*
     * 手指有上下滑動，不視為點擊。
     */
    if (hasMoved) {
      hasMoved = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.toggleMobileNavigation();
  });

  /*
   * 阻止列表內的 click 冒泡到 header。
   */
  content.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  header.dataset.mobileToggleInitialized = 'true';
}

  isMobile() {
    return window.matchMedia(
      `(max-width: ${this.mobileBreakpoint - 1}px)`
    ).matches;
  }

  openMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.add('is-open');
  }

  closeMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.remove('is-open');
  }

  toggleMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.toggle('is-open');
  }

  /**
   * Only reset the navigation when the responsive breakpoint changes.
   *
   * Mobile browsers trigger resize events while scrolling because the
   * browser address bar expands or collapses. Those resize events should
   * not close the product navigation.
   */
handleResize() {
  const currentMobileState = this.isMobile();

  /*
   * 手機瀏覽器上下滑動時，網址列縮放可能觸發 resize。
   * 只要沒有跨越 990px 斷點，就完全不處理。
   */
  if (currentMobileState === this.previousMobileState) {
    return;
  }

  this.previousMobileState = currentMobileState;

  if (currentMobileState) {
    /*
     * 從桌面切換到手機時，只清除舊桌面抽屜狀態。
     * 不主動移除 is-open。
     */
    this.navigation.classList.remove('mobile-drawer', 'open');
  } else {
    /*
     * 從手機切換到桌面時，才移除手機展開狀態。
     */
    this.navigation.classList.remove(
      'is-open',
      'mobile-drawer',
      'open'
    );
  }

  document.body.style.overflow = '';

  const overlay = document.querySelector('.collection-nav-overlay');

  if (overlay) {
    overlay.remove();
  }

  const trigger = document.querySelector(
    '.collection-nav-mobile-trigger'
  );

  if (trigger) {
    trigger.style.display = 'none';
  }

  const closeButton = this.navigation.querySelector(
    '.collection-nav-close'
  );

  if (closeButton) {
    closeButton.remove();
  }
}

  refresh() {
    this.details = document.querySelectorAll(
      '.collection-nav-details'
    );

    this.setupAccordion();
    this.setupAutoExpand();
    this.setupMobileCollapsible();
  }
}

function initializeCollectionNavigation(scope = document) {
  const navigation = scope.querySelector('.collection-navigation');

  if (!navigation) return;

  if (navigation.dataset.navigationInitialized === 'true') {
    return;
  }

  navigation.dataset.navigationInitialized = 'true';

  new CollectionNavigation(navigation);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeCollectionNavigation(document);
  });
} else {
  initializeCollectionNavigation(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initializeCollectionNavigation(event.target);
});
